import { pushToDataLayer } from '../analytics/analyticsTracking'
import {
	assertErrorOrThrowUnknown,
	extractErrorOrUnknown,
	formatPrefixedMessage,
} from './errorUtils'
import { ExpApiErrorObject, ExpApiErrorCode } from './models/errorTypes'

/**
 * Custom error class for Experience API errors.
 *
 * Extends the native Error class to include structured error details from the
 * Experience API, including error codes, request IDs, and HTTP status codes.
 * Provides convenient access to error metadata for logging and user feedback.
 */
export class ExpApiError extends Error {
	public readonly details: ExpApiErrorObject
	public readonly errorCd: ExpApiErrorObject['errorCd']
	public readonly reqId: ExpApiErrorObject['reqId']
	public readonly httpStatusCd: ExpApiErrorObject['httpStatusCd']
	public readonly errorMessageTx?: ExpApiErrorObject['errorMessageTx']

	/**
	 * Creates an ExpApiError instance from API error details.
	 *
	 * @param details - Structured error object from the Experience API
	 */
	constructor(details: ExpApiErrorObject) {
		super(details.errorMessageTx ?? details.errorCd)
		Object.setPrototypeOf(this, ExpApiError.prototype)
		this.name = 'ExpApiError'
		this.details = details
		// Mirror key payload properties on the instance for convenient runtime/TS access
		this.errorCd = details.errorCd
		this.reqId = details.reqId
		this.httpStatusCd = details.httpStatusCd
		this.errorMessageTx = details.errorMessageTx
	}
}

/**
 * Type guard to check if a value is an ExpApiErrorObject.
 *
 * Validates that the value contains the required properties for an Experience
 * API error object (errorCd, reqId, httpStatusCd).
 *
 * @param value - Value to check
 * @returns True if value is a valid ExpApiErrorObject
 */
export function isExpApiErrorObject(
	value: unknown
): value is ExpApiErrorObject {
	if (!value || typeof value !== 'object') return false
	return 'errorCd' in value && 'reqId' in value && 'httpStatusCd' in value
}

/**
 * Extracts ExpApiErrorObject details from various error shapes.
 *
 * Attempts to extract structured API error details from ExpApiError instances,
 * plain error objects, or nested details properties.
 *
 * @param error - Error value to extract details from
 * @returns ExpApiErrorObject if found, undefined otherwise
 */
function getExpApiDetailsFromUnknown(
	error: unknown
): ExpApiErrorObject | undefined {
	if (error instanceof ExpApiError) return error.details
	if (isExpApiErrorObject(error)) return error
	if (error && typeof error === 'object') {
		const { details } = error as { details?: unknown }
		if (isExpApiErrorObject(details)) return details
	}
	return undefined
}

/**
 * Pushes Experience API error events to the analytics data layer.
 *
 * Tracks API errors in analytics for monitoring and debugging purposes.
 *
 * @param details - Error details containing the error code
 */
function pushExpApiErrorToDataLayer(details?: {
	errorCd?: string
	reqId?: string
	errorMessageTx?: string
}) {
	pushToDataLayer(
		{
			event: 'ExpAPI Error',
			expapi_error_code: details?.errorCd ?? 'unknown',
			expapi_req_id: details?.reqId ?? 'unknown',
			expapi_message:
				(details?.errorMessageTx ?? '').slice(0, 200) || undefined,
		},
		true
	)
}

/**
 * URL patterns where a `SourceNotFoundError` response is an expected
 * first-time / empty-state signal, not a real error. Matching requests
 * will NOT emit an "ExpAPI Error" event to Amplitude.
 */
const SUPPRESSED_SOURCE_NOT_FOUND_URLS: Array<string | RegExp> = [
	// GET /api/users/userid/preferences — returns 400 SourceNotFoundError
	'/api/users/userid/preferences',
	// GET /api/companies/:companyId/goals — returns 400 SourceNotFoundError
	/\/api\/companies\/[^/]+\/goals/,
]

/**
 * Expected empty-state errors that should NOT be tracked as Amplitude
 * "ExpAPI Error" events. Scoped narrowly by (errorCd, requestUrl) so
 * legitimate SourceNotFoundError responses on other endpoints (BI, MLS,
 * quartile metrics, etc.) are still tracked.
 */
function isExpectedEmptyStateError(
	errorCd: string | undefined,
	requestUrl: string | undefined
): boolean {
	if (!errorCd || !requestUrl) return false
	if (errorCd !== ExpApiErrorCode.SourceNotFoundError) return false
	return SUPPRESSED_SOURCE_NOT_FOUND_URLS.some(pattern =>
		typeof pattern === 'string'
			? requestUrl.includes(pattern)
			: pattern.test(requestUrl)
	)
}

/**
 * Builds URL query parameters from Experience API error details.
 *
 * Constructs a query string containing error code and request ID for
 * error tracking and support purposes. Returns empty string if error
 * details cannot be extracted.
 *
 * @param error - Error value to extract details from
 * @returns URL-encoded query string or empty string
 */
export function buildExpApiQueryParams(error: unknown): string {
	const details = getExpApiDetailsFromUnknown(error)
	if (!details) return ''
	const ec = encodeURIComponent(details.errorCd)
	const rid = encodeURIComponent(details.reqId)
	return `?errorCd=${ec}&reqId=${rid}`
}

/**
 * Normalizes and re-throws errors as ExpApiError instances.
 *
 * Converts various error shapes into ExpApiError instances when possible,
 * falling back to standard error handling. Automatically pushes error
 * analytics to the data layer before throwing.
 *
 * @param error - Error value to normalize and throw
 * @param errorText - Additional error context text
 * @param requestUrl - Optional URL of the originating request; when provided,
 *   enables suppression of expected empty-state errors (see
 *   isExpectedEmptyStateError).
 * @throws Always throws either ExpApiError or normalized Error
 */
export function assertApiErrorOrThrow(
	error: unknown,
	errorText?: string,
	requestUrl?: string
): never {
	// Already an ExpApiError instance → the Amplitude event was already
	// pushed (or suppressed) when the error was first constructed in the
	// details branch below. Re-pushing here causes duplicate events,
	// especially when service-layer catches re-throw via this helper
	// without a requestUrl (which would prevent suppression even for
	// known empty-state endpoints). So simply re-throw.
	if (error instanceof ExpApiError) {
		throw error
	}

	// Unified extraction for plain object or nested details
	const details = getExpApiDetailsFromUnknown(error)
	if (details) {
		const suppressAmplitude = isExpectedEmptyStateError(
			details?.errorCd,
			requestUrl
		)
		if (!suppressAmplitude) {
			// If the original error object contained a nested `details` property,
			// expected by analytics consumers/tests.
			if (error && typeof error === 'object' && 'details' in error) {
				pushToDataLayer(
					{
						event: 'ExpAPI Error',
						expapi_error_code: details?.errorCd ?? 'unknown',
						expapi_req_id: details?.reqId ?? 'unknown',
						expapi_message:
							(details?.errorMessageTx ?? '').slice(0, 200) || undefined,
					},
					true
				)
			} else {
				pushExpApiErrorToDataLayer(details)
			}
		}
		throw new ExpApiError(details)
	}

	// Standard Error instance handling
	assertErrorOrThrowUnknown(
		error,
		errorText,
		ExpApiErrorCode.NETWORK_ERROR,
		ExpApiErrorCode.UNKNOWN
	)
}

/**
 * Formats error details into user-friendly support text.
 *
 * Creates an array of formatted strings containing error code and request ID
 * for display in error messages or support documentation.
 *
 * @param errorCd - Experience API error code
 * @param reqId - Request ID associated with the error
 * @returns Array of formatted support text strings
 */
export function formatErrorSupportText(
	errorCd: ExpApiErrorCode,
	reqId: string
): string[] {
	return [`For support - errorCd: ${errorCd}`, `reqId: ${reqId}`]
}

/**
 * Extracts and enriches API error details without throwing.
 *
 * Attempts to extract structured error details from various error shapes,
 * preserving ExpApiError instances or enriching plain error objects with
 * additional context. Falls back to standard error extraction when API
 * error details are not available.
 *
 * @param error - Error value to extract details from
 * @param errorText - Additional error context to prepend to error message
 * @returns ExpApiError instance, enriched ExpApiErrorObject, or normalized Error
 */
export function extractApiErrorDetails(
	error: unknown,
	errorText?: string
): ExpApiErrorObject | Error | ExpApiError {
	// If already an ExpApiError instance, return it (preserve instanceof)
	if (error instanceof ExpApiError) {
		return error
	}

	// Use unified extractor for object shapes / nested details
	const details = getExpApiDetailsFromUnknown(error)
	if (details) {
		const errorMessageTx = formatPrefixedMessage(
			errorText,
			details.errorMessageTx
		)
		return { ...details, errorMessageTx }
	}

	return extractErrorOrUnknown(error, errorText)
}

/**
 * Utility method to convert an companyContextError to comapny error support lines
 * @param options - Array of objects with label and value properties
 * @returns String array of support lines or undefined
 */

export const formatCompanyErrorSupport = (
	companyContextError: Error | ExpApiErrorObject | ExpApiError | undefined
) => {
	const hasError = Boolean(companyContextError)
	let supportLines: string[] | undefined
	if (
		hasError &&
		(companyContextError instanceof ExpApiError ||
			isExpApiErrorObject(companyContextError))
	) {
		supportLines = formatErrorSupportText(
			companyContextError?.errorCd,
			companyContextError?.reqId
		)
	}
	return supportLines
}